import type { PersonThumbnailBatch } from '@sentra/contracts';
import { MAX_THUMBNAILS_PER_REQUEST } from '@sentra/contracts';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

import type { ApiClient } from '@/api/client';
import { isApiError, TransportError } from '@/api/errors';
import { queryKeys } from '@/api/query-keys';
import { apiRoutes } from '@/api/routes';

/**
 * Every face this browser draws, a page of them per request.
 *
 * **Why this exists.** The roll-call, the outside monitor, the dining roster and the register drew
 * one photograph per row, and each was an `<img>` pointing at the single-image route: one request
 * per face. Opening the dashboard of a full residence sent hundreds in a second, and the per-address
 * limit every terminal behind the building's one address shares refused them all — the gates
 * included — for a minute. Here a render's worth of faces is collected for a moment, de-duplicated,
 * and asked for in batches of {@link MAX_THUMBNAILS_PER_REQUEST}, no more than two at a time.
 *
 * **What a thumbnail resolves to is decided by the server's ROW**: `ready` with the bytes, or
 * `missing` — no such image any more, or an erased record — which is drawn as "could not be
 * loaded" rather than as the "no photograph" square a list draws for a person who never had one.
 * Anything the server could not read NOW (its row says the photograph exists) is an error, retried
 * a few times by the query and then shown, never quietly drawn as an empty square: at a gate, a
 * face that silently failed to appear is the one an operator does not know to look for.
 *
 * **No HTTP or disk cache is involved.** Every answer is `no-store`; what holds a face is this
 * page's memory, through the query cache, for as long as the retention class of its key allows.
 */

/** A thumbnail's answer, as a screen draws it. */
export type ThumbnailResult =
  { readonly state: 'ready'; readonly src: string } | { readonly state: 'missing' };

/** A photograph the server's row says exists and that could not be read just now. Retried. */
export class ThumbnailUnavailableError extends Error {
  constructor(imageId: string) {
    super(`the thumbnail of ${imageId} could not be read now`);
    this.name = 'ThumbnailUnavailableError';
  }
}

/** Why a waiter was let go without an answer: its screen let go of it, or the operator changed. */
class ThumbnailAbandonedError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = 'ThumbnailAbandonedError';
  }
}

/** How long a render's requests are collected before the first batch is sent. */
export const THUMBNAIL_COLLECT_MS = 10;
/** How many batches may be in flight at once. Four would fit the server's ceiling; two is polite. */
export const THUMBNAIL_BATCHES_IN_FLIGHT = 2;
/** How long one batch may take before it is abandoned and its faces retried. */
export const THUMBNAIL_BATCH_TIMEOUT_MS = 20_000;

export interface ThumbnailLoaderOptions {
  /** Asks the server for one batch. The signal fires when the batch is abandoned. */
  readonly fetchBatch: (
    imageIds: readonly string[],
    signal: AbortSignal,
  ) => Promise<PersonThumbnailBatch>;
  /** Test seam: returns a cancel. Defaults to `setTimeout`. */
  readonly schedule?: (work: () => void, ms: number) => () => void;
}

export interface ThumbnailLoader {
  /** The thumbnail of one image. Rejects when it could not be read, or when `signal` fires. */
  readonly load: (imageId: string, signal?: AbortSignal) => Promise<ThumbnailResult>;
  /**
   * Forgets every pending and in-flight request, rejecting whoever was waiting.
   *
   * Called when a different operator takes the terminal, BEFORE the query cache is reset — the
   * reset asks every mounted view again at once, and those new requests must not attach to a batch
   * the previous operator's session sent, nor be rejected by this.
   */
  readonly reset: () => void;
}

interface Waiter {
  readonly resolve: (result: ThumbnailResult) => void;
  readonly reject: (reason: unknown) => void;
}

function defaultSchedule(work: () => void, ms: number): () => void {
  const handle = globalThis.setTimeout(work, ms);
  return () => {
    globalThis.clearTimeout(handle);
  };
}

/**
 * The longest a refusal may hold the page's faces back: the longest block this server imposes.
 *
 * `Retry-After` is read from a header, and a proxy or a broken answer that asked for an hour would
 * otherwise leave every face on every screen blank for that hour, with nothing to say why.
 */
const MAX_PAUSE_MS = 60_000;

/** The wait a refusal asked for before anything else is sent, or `null` when it asked for none. */
function pauseFor(error: unknown): number | null {
  if (!isApiError(error)) {
    return null;
  }
  if (error.code === 'SYSTEM_RATE_LIMITED' || error.code === 'SYSTEM_STORAGE_BUSY') {
    return Math.min((error.retryAfterSeconds ?? 1) * 1000, MAX_PAUSE_MS);
  }
  return null;
}

export function createThumbnailLoader(options: ThumbnailLoaderOptions): ThumbnailLoader {
  const schedule = options.schedule ?? defaultSchedule;
  /** Everybody waiting on each image, counted per image so one leaving strands nobody else. */
  const waiters = new Map<string, Set<Waiter>>();
  /** Ids asked for and not yet sent, in the order they were asked. */
  const queued: string[] = [];
  /** Ids in a batch that has not answered yet, which a new waiter joins rather than re-sending. */
  const sent = new Set<string>();
  let inFlight = 0;
  let paused = false;
  let cancelPause: () => void = () => undefined;
  let collecting: (() => void) | null = null;
  /** Bumped by `reset`, so an answer to a forgotten batch settles nobody. */
  let generation = 0;

  const settle = (
    imageId: string,
    outcome: ThumbnailResult | { readonly error: unknown },
  ): void => {
    const waiting = waiters.get(imageId);
    waiters.delete(imageId);
    for (const waiter of waiting ?? []) {
      if ('error' in outcome) {
        waiter.reject(outcome.error);
      } else {
        waiter.resolve(outcome);
      }
    }
  };

  /** Settles every face a batch named from what the server answered. */
  const settleAnswer = (batch: readonly string[], answer: PersonThumbnailBatch): void => {
    const answered = new Set<string>();
    for (const item of answer.items) {
      answered.add(item.imageId);
      settle(item.imageId, { state: 'ready', src: `data:image/jpeg;base64,${item.jpegBase64}` });
    }
    for (const imageId of answer.missing) {
      answered.add(imageId);
      settle(imageId, { state: 'missing' });
    }
    // `unavailable`, and anything the server did not mention at all, could not be read now.
    for (const imageId of batch) {
      if (!answered.has(imageId)) {
        settle(imageId, { error: new ThumbnailUnavailableError(imageId) });
      }
    }
  };

  /** Holds every later batch back for as long as a refusal asked. */
  const pauseDispatch = (ms: number): void => {
    // The whole loader waits, not just this batch: the next batch would be refused by the same
    // limit, and every refusal is a request against it.
    paused = true;
    cancelPause();
    cancelPause = schedule(() => {
      paused = false;
      dispatch();
    }, ms);
  };

  /** Settles every face of a batch that produced no answer, with ONE error between them. */
  const settleFailure = (batch: readonly string[], error: unknown): void => {
    const pause = pauseFor(error);
    if (pause !== null) {
      pauseDispatch(pause);
    }
    // The same instance for every image of the batch, so whatever reads it — the session
    // overlay, the reset on unlock — sees one refusal rather than twenty-five.
    for (const imageId of batch) {
      settle(imageId, { error });
    }
  };

  const send = async (batch: readonly string[], sentIn: number): Promise<void> => {
    const controller = new AbortController();
    const cancelTimeout = schedule(() => {
      controller.abort();
    }, THUMBNAIL_BATCH_TIMEOUT_MS);
    try {
      const answer = await options.fetchBatch(batch, controller.signal);
      if (sentIn === generation) {
        settleAnswer(batch, answer);
      }
    } catch (failure) {
      if (sentIn === generation) {
        settleFailure(
          batch,
          controller.signal.aborted
            ? new TransportError('a thumbnail batch took too long and was abandoned')
            : failure,
        );
      }
    } finally {
      cancelTimeout();
      if (sentIn === generation) {
        for (const imageId of batch) {
          sent.delete(imageId);
        }
        inFlight -= 1;
        dispatch();
      }
    }
  };

  function dispatch(): void {
    while (!paused && inFlight < THUMBNAIL_BATCHES_IN_FLIGHT && queued.length > 0) {
      const batch = queued.splice(0, MAX_THUMBNAILS_PER_REQUEST);
      for (const imageId of batch) {
        sent.add(imageId);
      }
      inFlight += 1;
      void send(batch, generation);
    }
  }

  const collect = (): void => {
    if (collecting !== null) {
      return;
    }
    collecting = schedule(() => {
      collecting = null;
      dispatch();
    }, THUMBNAIL_COLLECT_MS);
  };

  const abandon = (imageId: string, waiter: Waiter): void => {
    const waiting = waiters.get(imageId);
    if (waiting?.delete(waiter) !== true) {
      return;
    }
    waiter.reject(new ThumbnailAbandonedError('the screen that asked no longer wants it'));
    if (waiting.size > 0) {
      return;
    }
    waiters.delete(imageId);
    // Nobody else wants it: an id not yet sent is not sent. One already in flight is left to land,
    // because cancelling a shared batch would cost every other face in it.
    const at = queued.indexOf(imageId);
    if (at !== -1) {
      queued.splice(at, 1);
    }
  };

  return {
    load: (imageId, signal) =>
      new Promise<ThumbnailResult>((resolve, reject) => {
        if (signal?.aborted === true) {
          reject(new ThumbnailAbandonedError('asked for after its screen had let go'));
          return;
        }
        const waiter: Waiter = { resolve, reject };
        const waiting = waiters.get(imageId) ?? new Set<Waiter>();
        waiting.add(waiter);
        waiters.set(imageId, waiting);
        signal?.addEventListener(
          'abort',
          () => {
            abandon(imageId, waiter);
          },
          { once: true },
        );
        if (!sent.has(imageId) && !queued.includes(imageId)) {
          queued.push(imageId);
        }
        collect();
      }),
    reset: () => {
      generation += 1;
      collecting?.();
      collecting = null;
      cancelPause();
      paused = false;
      inFlight = 0;
      queued.length = 0;
      sent.clear();
      const forgotten = [...waiters.keys()];
      for (const imageId of forgotten) {
        settle(imageId, { error: new ThumbnailAbandonedError('a different operator took over') });
      }
    },
  };
}

/**
 * The page's loader: one per query cache, which is one per page, because the batching is
 * page-wide by design.
 *
 * It asks the server through whichever client the session held when the face was last asked for.
 * The session provider builds a new client on every state or locale change, and the query
 * function — never a render — is what hands the current one over.
 */
interface PageThumbnails {
  readonly loader: ThumbnailLoader;
  client: ApiClient | null;
}

const PAGES = new WeakMap<QueryClient, PageThumbnails>();

function pageFor(queries: QueryClient): PageThumbnails {
  const existing = PAGES.get(queries);
  if (existing !== undefined) {
    return existing;
  }
  const page: PageThumbnails = {
    client: null,
    loader: createThumbnailLoader({
      fetchBatch: (imageIds, signal) => {
        if (page.client === null) {
          return Promise.reject(new TransportError('no session client has asked for a face yet'));
        }
        return page.client.request(apiRoutes.personThumbnails, {
          query: { ids: imageIds.join(',') },
          signal,
        });
      },
    }),
  };
  PAGES.set(queries, page);
  return page;
}

/** Forgets the page's pending thumbnails, if it has any. See {@link ThumbnailLoader.reset}. */
export function resetThumbnails(queries: QueryClient): void {
  PAGES.get(queries)?.loader.reset();
}

/** Who a thumbnail is held for, which is part of its cache key (`api/query-keys.ts`). */
export type ThumbnailRetention = 'desk' | 'gate';

/** How long a desk screen's thumbnail outlives the last view drawing it. */
const DESK_THUMBNAIL_GC_MS = 5 * 60_000;

/** Whether a failed thumbnail is worth asking for again. A refusal about the session is not. */
function retryable(error: unknown): boolean {
  if (error instanceof ThumbnailUnavailableError || error instanceof TransportError) {
    return true;
  }
  return (
    isApiError(error) &&
    (error.code === 'SYSTEM_RATE_LIMITED' ||
      error.code === 'SYSTEM_STORAGE_BUSY' ||
      error.code === 'SYSTEM_DEPENDENCY_UNAVAILABLE')
  );
}

/** At most this many retries, so a face that cannot be read is shown as such rather than spinning. */
const THUMBNAIL_RETRIES = 3;

/** The query that holds one thumbnail, in one retention class. */
export function thumbnailQuery(
  queries: QueryClient,
  client: ApiClient,
  imageId: string,
  retention: ThumbnailRetention,
): {
  readonly queryKey: QueryKey;
  readonly queryFn: (context: { readonly signal: AbortSignal }) => Promise<ThumbnailResult>;
  readonly staleTime: number;
  readonly gcTime: number;
  readonly refetchOnWindowFocus: false;
  readonly refetchOnReconnect: false;
  readonly retry: (failureCount: number, error: unknown) => boolean;
  readonly retryDelay: (failureCount: number, error: unknown) => number;
} {
  return {
    queryKey:
      retention === 'gate' ? queryKeys.gateThumbnail(imageId) : queryKeys.personThumbnail(imageId),
    queryFn: ({ signal }) => {
      const page = pageFor(queries);
      page.client = client;
      return page.loader.load(imageId, signal);
    },
    // An image id names one stored photograph forever: a replacement is a new id, a removal is a
    // write this browser evicts on (`api/invalidation.ts`). Nothing about it ever goes stale.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: retention === 'gate' ? 0 : DESK_THUMBNAIL_GC_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => retryable(error) && failureCount < THUMBNAIL_RETRIES,
    retryDelay: (failureCount, error) => {
      const pause = pauseFor(error);
      return pause ?? Math.min(1000 * 2 ** failureCount, 8000);
    },
  };
}
